import { CitationButton } from '@/features/common/CitationButton';
import { RESOURCE_TYPE } from '@/features/common/resourceType';
import { SaveButton } from '@/features/common/SaveButton';
import { VideoPlayer } from '@/features/common/VideoPlayer';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import { registryApi } from '@/features/publicsite/registry/api/registryApi';
import {
  libraryTypeLabelKey,
  resourceSubtitle,
} from '@/features/publicsite/registry/libraryListing';
import type { LibraryResourceCardProps } from '@/features/publicsite/types/registry/LibraryResourceCardProps';

/** A library resource's card: what it is, its abstract, and FR-021's download, save and cite. */
export function LibraryResourceCard({
  resource: r,
  canSave,
  saved,
  onSavedChange,
}: LibraryResourceCardProps) {
  const { t } = useTranslation(publicRegistry);
  const typeKey = libraryTypeLabelKey(r.type);
  return (
    <CatalogCard
      // Was the raw enum, so a card read "TRAINING_MATERIAL". Same strings the type filter
      // offers, so the badge and the filter option name the same thing.
      badge={typeKey ? t(typeKey) : `libraryType${r.type}`}
      title={r.title}
      subtitle={resourceSubtitle(r)}
    >
      {r.abstractText && <p>{r.abstractText}</p>}
      {/* FR-022 asks for upload, download AND in-platform viewing. The player was written
          and imported nowhere, so a video was only ever offered as a file to save. Both are
          offered now: the download link below stays. */}
      {r.type === RESOURCE_TYPE.video && r.mediaAssetId && (
        <VideoPlayer mediaAssetId={r.mediaAssetId} title={r.title} />
      )}
      <div className="card__actions">
        {r.mediaAssetId && (
          // Through the library, not straight at the media store: the media endpoint knows
          // nothing about resources, so a direct link counted nothing and access_count only
          // ever moved when someone generated a citation. This redirects to the same file.
          <a className="btn btn--ghost" href={registryApi.libraryDownloadUrl(r.id)}>
            {t('libraryDownload')}
          </a>
        )}
        {r.externalSource && (
          <a className="btn btn--ghost" href={r.externalSource} target="_blank" rel="noopener">
            {t('librarySource')}
            <span className="visually-hidden"> {t('libraryNewTab')}</span>
          </a>
        )}
        {/* FR-021 names three actions on a resource — download, save and cite. The formatter
            for this one already existed and answered correctly; nothing called it. */}
        <CitationButton resourceId={r.id} />
        {canSave && <SaveButton resourceId={r.id} saved={saved} onChange={onSavedChange} />}
      </div>
    </CatalogCard>
  );
}
